import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Lock,
  LogOut,
  Download,
  RefreshCw,
  FileText,
  Image as ImageIcon,
  Inbox,
} from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const EMAIL_BADGE = {
  sent: "bg-[#E4F2E8] text-[#1E5B33]",
  logged: "bg-mist text-steel",
  failed: "bg-[#FBE3E3] text-[#8C1D1D]",
  pending: "bg-[#FDF3E3] text-[#7A5800]",
  not_requested: "bg-mist text-steel",
};

function Badge({ children, tone }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${EMAIL_BADGE[tone] || "bg-mist text-steel"}`}
    >
      {children}
    </span>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null = checking
  const [emailMode, setEmailMode] = useState("log");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [tab, setTab] = useState("claims");
  const [claims, setClaims] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [c, e] = await Promise.all([
        axios.get(`${API}/admin/claims`),
        axios.get(`${API}/admin/enquiries`),
      ]);
      setClaims(c.data.claims);
      setEnquiries(e.data.enquiries);
    } catch (err) {
      if (err.response?.status === 401) setAuthed(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/admin/me`)
      .then((r) => {
        setAuthed(true);
        setEmailMode(r.data.email_mode || "log");
        loadData();
      })
      .catch(() => setAuthed(false));
  }, [loadData]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      await axios.post(`${API}/admin/login`, { password });
      const me = await axios.get(`${API}/admin/me`);
      setEmailMode(me.data.email_mode || "log");
      setAuthed(true);
      loadData();
    } catch {
      setLoginError("Incorrect password.");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/admin/logout`).catch(() => {});
    setAuthed(false);
  };

  const setStatus = async (reference, status) => {
    setClaims((prev) =>
      prev.map((c) => (c.reference === reference ? { ...c, status } : c))
    );
    try {
      await axios.post(`${API}/admin/claims/${reference}/status`, { status });
    } catch {
      loadData();
    }
  };

  const resend = async (reference) => {
    setResending(reference);
    try {
      const r = await axios.post(`${API}/admin/claims/${reference}/resend`);
      setClaims((prev) =>
        prev.map((c) =>
          c.reference === reference ? { ...c, email_status: r.data.email_status } : c
        )
      );
    } catch {
      // leave status as failed
    } finally {
      setResending("");
    }
  };

  const inputCls =
    "w-full min-h-[48px] rounded border border-ink/15 bg-white px-4 py-3 text-base text-ink focus:outline-none focus:ring-2 focus:ring-road focus:border-road";

  if (authed === null) {
    return (
      <div className="min-h-screen bg-mist flex items-center justify-center">
        <p className="text-steel" data-testid="admin-loading">Loading…</p>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-mist flex items-center justify-center px-4">
        <Seo title="Admin | Reliance Motor Claims Recovery" description="" path="/admin" noindex />
        <form
          onSubmit={login}
          className="w-full max-w-sm bg-white rounded-2xl p-8 border-l-8 border-road shadow-sm"
        >
          <span className="inline-flex items-center justify-center size-12 bg-mist rounded-lg">
            <Lock className="size-6 text-ink" aria-hidden="true" />
          </span>
          <h1 className="mt-4 font-saira font-semibold text-2xl text-ink">Claims admin</h1>
          <p className="mt-1 text-sm text-steel">Enter the admin password to continue.</p>
          <label htmlFor="admin-password" className="block text-sm font-semibold text-ink mt-6 mb-1.5">
            Password
          </label>
          <input
            id="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputCls}
            autoComplete="current-password"
            data-testid="admin-password-input"
          />
          {loginError && (
            <p className="mt-3 text-sm font-semibold text-[#8C1D1D]" role="alert" data-testid="admin-login-error">
              {loginError}
            </p>
          )}
          <button
            type="submit"
            className="mt-6 w-full inline-flex items-center justify-center min-h-[48px] px-6 bg-ink text-white font-saira font-bold rounded hover:bg-black transition-colors"
            data-testid="admin-login-button"
          >
            Log in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mist">
      <Seo title="Admin | Reliance Motor Claims Recovery" description="" path="/admin" noindex />
      <div className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between gap-4">
          <div>
            <h1 className="font-saira font-semibold text-xl" data-testid="admin-heading">
              Claims admin
            </h1>
            <p className="text-xs text-white/60 mt-0.5" data-testid="admin-email-mode">
              Email mode: {emailMode === "live" ? "live (Resend)" : "log-only (RESEND_API_KEY not set)"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="hidden sm:inline-flex items-center min-h-[40px] px-3 text-sm text-white/70 hover:text-white"
              data-testid="admin-back-to-site"
            >
              Back to site
            </a>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 min-h-[40px] px-4 bg-white/10 rounded text-sm font-semibold hover:bg-white/20 transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut className="size-4" aria-hidden="true" /> Log out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-2 border-b border-ink/10" role="tablist">
          <button
            role="tab"
            aria-selected={tab === "claims"}
            onClick={() => setTab("claims")}
            className={`inline-flex items-center gap-2 min-h-[48px] px-4 font-saira font-semibold text-sm border-b-4 -mb-px transition-colors ${
              tab === "claims" ? "border-road text-ink" : "border-transparent text-steel hover:text-ink"
            }`}
            data-testid="admin-tab-claims"
          >
            <FileText className="size-4" aria-hidden="true" /> Claim submissions ({claims.length})
          </button>
          <button
            role="tab"
            aria-selected={tab === "enquiries"}
            onClick={() => setTab("enquiries")}
            className={`inline-flex items-center gap-2 min-h-[48px] px-4 font-saira font-semibold text-sm border-b-4 -mb-px transition-colors ${
              tab === "enquiries" ? "border-road text-ink" : "border-transparent text-steel hover:text-ink"
            }`}
            data-testid="admin-tab-enquiries"
          >
            <Inbox className="size-4" aria-hidden="true" /> Enquiries ({enquiries.length})
          </button>
        </div>

        {loading && <p className="mt-8 text-steel" data-testid="admin-loading-data">Loading…</p>}

        {!loading && tab === "claims" && (
          <div className="mt-6 space-y-4" data-testid="admin-claims-list">
            {claims.length === 0 && (
              <p className="text-steel py-10 text-center" data-testid="admin-claims-empty">
                No claim submissions yet.
              </p>
            )}
            {claims.map((c) => (
              <div
                key={c.id}
                className="bg-white rounded-lg p-5 sm:p-6 border-l-4 border-road"
                data-testid={`admin-claim-${c.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-saira font-bold text-lg text-ink">
                      {c.vehicle_rego || "Claim"}{c.customer.name ? ` – ${c.customer.name}` : ""}
                    </p>
                    <p className="text-sm text-steel mt-0.5">{c.received_at}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone={c.email_status}>email: {c.email_status}</Badge>
                    <Badge tone={c.customer_copy_status}>copy: {c.customer_copy_status.replace("_", " ")}</Badge>
                  </div>
                </div>

                <dl className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-steel">Customer</dt>
                    <dd className="font-semibold text-ink">{c.customer.name || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-steel">Phone</dt>
                    <dd className="font-semibold text-ink">{c.customer.phone || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-steel">Rego</dt>
                    <dd className="font-semibold text-ink">{c.vehicle_rego || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-steel">Accident date</dt>
                    <dd className="font-semibold text-ink">{c.accident_date || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-steel">At-fault rego</dt>
                    <dd className="font-semibold text-ink">{c.at_fault_rego || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-steel">Licence attached</dt>
                    <dd className="font-semibold text-ink" data-testid={`admin-licence-count-${c.id}`}>
                      {c.licence_count > 0 ? c.licence_count : "No"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-steel">Registration attached</dt>
                    <dd className="font-semibold text-ink" data-testid={`admin-registration-count-${c.id}`}>
                      {c.registration_count > 0 ? c.registration_count : "No"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-steel">Photos</dt>
                    <dd className="font-semibold text-ink" data-testid={`admin-photo-count-${c.id}`}>
                      {c.photo_count}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-steel">Diagram image</dt>
                    <dd className="font-semibold text-ink" data-testid={`admin-diagram-count-${c.id}`}>
                      {c.diagram_count > 0 ? "Yes" : "No"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-steel">Repairer copy sent</dt>
                    <dd className="font-semibold text-ink" data-testid={`admin-repairer-copy-${c.id}`}>
                      {c.repairer_copy_status === "sent" || c.repairer_copy_status === "logged" ? "Yes" : "No"}
                    </dd>
                  </div>
                </dl>

                {c.submitted_by?.type === "repairer" && (
                  <p className="mt-3 text-sm text-steel" data-testid={`admin-submitted-by-${c.id}`}>
                    Submitted by repairer:{" "}
                    <span className="font-semibold text-ink">
                      {c.submitted_by.business || "—"}
                      {[c.submitted_by.contact_name, c.submitted_by.phone, c.submitted_by.email]
                        .filter(Boolean)
                        .length > 0 &&
                        ` (${[c.submitted_by.contact_name, c.submitted_by.phone, c.submitted_by.email].filter(Boolean).join(", ")})`}
                    </span>
                  </p>
                )}

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <a
                    href={`${API}/admin/claims/${c.id}/pdf`}
                    className="inline-flex items-center gap-2 min-h-[44px] px-4 bg-ink text-white text-sm font-saira font-semibold rounded hover:bg-black transition-colors"
                    data-testid={`admin-download-pdf-${c.id}`}
                  >
                    <Download className="size-4" aria-hidden="true" /> Download PDF
                  </a>
                  {c.photos.map((p) => (
                    <a
                      key={p.index}
                      href={`${API}/admin/claims/${c.id}/photos/${p.index}`}
                      className="inline-flex items-center gap-2 min-h-[44px] px-4 bg-mist text-ink text-sm font-saira font-semibold rounded hover:bg-[#E2E5E9] transition-colors"
                      data-testid={`admin-download-photo-${c.id}-${p.index}`}
                    >
                      <ImageIcon className="size-4" aria-hidden="true" /> {p.filename}
                    </a>
                  ))}
                  {c.email_status === "failed" && (
                    <button
                      onClick={() => resend(c.id)}
                      disabled={resending === c.id}
                      className="inline-flex items-center gap-2 min-h-[44px] px-4 bg-[#8C1D1D] text-white text-sm font-saira font-semibold rounded hover:bg-[#6E1616] transition-colors disabled:opacity-60"
                      data-testid={`admin-resend-${c.id}`}
                    >
                      <RefreshCw className={`size-4 ${resending === c.id ? "animate-spin" : ""}`} aria-hidden="true" />
                      {resending === c.id ? "Resending…" : "Resend email"}
                    </button>
                  )}
                  <label className="inline-flex items-center gap-2 text-sm text-steel ml-auto">
                    Status
                    <select
                      value={c.status}
                      onChange={(e) => setStatus(c.id, e.target.value)}
                      className="min-h-[44px] rounded border border-ink/15 bg-white px-3 text-sm font-semibold text-ink focus:outline-none focus:ring-2 focus:ring-road"
                      data-testid={`admin-status-select-${c.id}`}
                    >
                      <option>New</option>
                      <option>In progress</option>
                      <option>Done</option>
                    </select>
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "enquiries" && (
          <div className="mt-6 space-y-4" data-testid="admin-enquiries-list">
            {enquiries.length === 0 && (
              <p className="text-steel py-10 text-center" data-testid="admin-enquiries-empty">
                No enquiries yet.
              </p>
            )}
            {enquiries.map((e, i) => (
              <div
                key={`${e.received_ts}-${i}`}
                className="bg-white rounded-lg p-5 sm:p-6 border-l-4 border-road"
                data-testid={`admin-enquiry-${i}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-saira font-bold text-lg text-ink">{e.name}</p>
                    <p className="text-sm text-steel mt-0.5">{e.received_at}</p>
                  </div>
                  <Badge tone={e.email_status}>email: {e.email_status}</Badge>
                </div>
                <p className="mt-2 text-sm text-steel">
                  {e.phone && <span className="mr-4">{e.phone}</span>}
                  {e.email && <span className="break-all">{e.email}</span>}
                </p>
                <p className="mt-3 text-base text-ink whitespace-pre-wrap">{e.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
